import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { eventService } from '../services/apiClient';
import EventCard from '../components/EventCard';
import { Star, Calendar } from 'lucide-react';

const WelcomeScreen = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
      return;
    }
    fetchUpcomingEvents();
  }, [isAuthenticated]);

  const fetchUpcomingEvents = async () => {
    try {
      const response = await eventService.getAllEvents();
      const now = new Date();
      const events = (response.data.events || [])
        .filter(e => new Date(e.date) > now)
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 3);
      setUpcomingEvents(events);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div
        className="h-[70vh] flex flex-col items-center justify-center p-4 relative overflow-hidden"
        style={{
          backgroundImage: 'url(/college-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"></div>

        <div className="relative z-10 text-center max-w-3xl mx-auto px-4">
          <h1 className="text-6xl md:text-8xl font-black text-white mb-6 tracking-tighter animate-in fade-in slide-in-from-bottom-8 duration-1000">
            COLLEGE<span className="text-blue-400">SPHERE</span>
          </h1>

          <p className="text-xl md:text-2xl text-blue-100 mb-10 font-medium leading-relaxed drop-shadow-md animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-200">
            The ultimate hub for G. Pulla Reddy Engineering College. <br />
            <span className="text-white font-bold italic">All Clubs. All Events. One Platform.</span>
          </p>

          <button
            onClick={() => navigate('/login-selection')}
            className="group relative inline-flex items-center justify-center px-12 py-5 font-bold text-white transition-all duration-300 bg-blue-600 rounded-2xl hover:bg-blue-700 hover:scale-105 active:scale-95 shadow-[0_20px_40px_rgba(37,99,235,0.3)] animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-500"
          >
            <span className="mr-3 text-xl">Enter Portal</span>
            <svg className="w-6 h-6 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Highlights Section */}
      <div className="max-w-7xl mx-auto px-4 py-20">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-4">
          <div>
            <div className="flex items-center gap-2 text-blue-600 mb-4">
              <Star className="w-5 h-5 fill-blue-600" />
              <span className="text-sm font-black uppercase tracking-[0.2em]">What's Happening</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">Upcoming Highlights</h2>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-white border-2 border-gray-100 rounded-2xl text-sm font-bold text-gray-600 hover:border-blue-600 hover:text-blue-600 transition-all flex items-center gap-2 shadow-sm"
          >
            <Calendar className="w-4 h-4" />
            Join to Register
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {upcomingEvents.length > 0 ? (
            upcomingEvents.map((event) => (
              <div key={event._id} className="relative">
                <EventCard event={event} onRegister={() => navigate('/login')} />
                {/* Overlay to intercept clicks if not logged in */}
                <div
                  className="absolute inset-x-0 bottom-0 h-20 cursor-pointer"
                  onClick={() => navigate('/login')}
                ></div>
              </div>
            ))
          ) : (
            <div className="col-span-full py-20 text-center bg-gray-50 rounded-[3rem] border-2 border-dashed border-gray-200">
              <p className="text-gray-400 text-lg font-medium">No events scheduled at the moment. Stay tuned!</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Access Footer */}
      <div className="bg-white border-t border-gray-100 py-12">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
          <div className="flex items-start gap-4">
            <span className="text-3xl bg-blue-50 p-4 rounded-2xl">🏛️</span>
            <div>
              <h3 className="font-bold text-gray-900">50+ Clubs</h3>
              <p className="text-gray-500 text-sm">Technical, Cultural & Social</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="text-3xl bg-purple-50 p-4 rounded-2xl">📅</span>
            <div>
              <h3 className="font-bold text-gray-900">Weekly Events</h3>
              <p className="text-gray-500 text-sm">Workshops, Hackathons & Fests</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="text-3xl bg-green-50 p-4 rounded-2xl">🤝</span>
            <div>
              <h3 className="font-bold text-gray-900">Community</h3>
              <p className="text-gray-500 text-sm">Collaborate with peers</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
